import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '../api.js';
import { useSession } from '../session.js';
import { Drawer, FindingList, Pill, Spinner, ErrorNote } from '../ui.js';
import { ChevronDown } from '../icons.js';
import { ReservationDrawer } from './ReservationDrawer.js';
import { NewReservation } from './Book.js';
import { ResourceGrid, longDate, shiftDate, type DayBoard } from './ResourceGrid.js';
import { useLive } from '../live.js';
import type { QueuedRequest } from './RequestQueue.js';

export function Schedule(): JSX.Element {
  const { can, role } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [date, setDate] = useState(() => params.get('date') ?? '');
  const [scope, setScope] = useState<'all' | 'mine'>(role === 'instructor' ? 'mine' : 'all');
  const [openId, setOpenId] = useState<number | null>(() => {
    const id = params.get('reservation');
    return id ? Number(id) : null;
  });

  // The booking form opens over the board. `assign` carries a request the inbox
  // handed over; `new` is a blank one.
  const assignId = params.get('assign');
  const booking = params.get('new') === '1' || !!assignId;
  const assignQuery = useQuery<QueuedRequest>(assignId ? `/schedule/reservations/${assignId}` : null);
  const closeBooking = () => {
    const next = new URLSearchParams(params);
    next.delete('new');
    next.delete('assign');
    setParams(next, { replace: true });
  };
  const openBooking = () => {
    const next = new URLSearchParams(params);
    next.set('new', '1');
    setParams(next, { replace: true });
  };

  // Requests are assigned on the reservation tab; the board only says how many
  // are waiting, so they are not invisible from here.
  const queue = useQuery<{ requests: unknown[] }>('/schedule/requests');
  const waiting = queue.data?.requests.length ?? 0;

  const dayQuery = useQuery<DayBoard>(
    `/schedule/day?scope=${scope}${date ? `&date=${date}` : ''}`,
  );

  // Someone else moving a lesson redraws this board where it stands.
  useLive('schedule', () => { dayQuery.reload(); queue.reload(); });

  const board = dayQuery.data;
  const effectiveDate = date || board?.date || '';

  const reload = useCallback(() => {
    dayQuery.reload();
  }, [dayQuery]);

  const goto = (href: string) => {
    const [path, query] = href.split('?');
    const search = new URLSearchParams(query ?? '');
    if (path === '/schedule') {
      if (search.get('date')) setDate(search.get('date')!);
      if (search.get('reservation')) setOpenId(Number(search.get('reservation')));
      return;
    }
    navigate(href);
  };

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (effectiveDate) next.set('date', effectiveDate); else next.delete('date');
    if (openId) next.set('reservation', String(openId)); else next.delete('reservation');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDate, openId]);

  if (dayQuery.error) return <ErrorNote message={dayQuery.error} />;

  return (
    <>
      <div className="toolbar">
        <div className="row">
          <div className="segmented">
            <button onClick={() => setDate(shiftDate(effectiveDate, -1))} aria-label="Previous day">‹</button>
            <button onClick={() => setDate('')} style={{ fontWeight: 700 }}>Today</button>
            <button onClick={() => setDate(shiftDate(effectiveDate, 1))} aria-label="Next day">›</button>
          </div>
          <div className="toolbar__title">{effectiveDate ? longDate(effectiveDate) : '—'}</div>
          <span className="toolbar__sub">
            {board?.sunrise && `sunrise ${clock(board.sunrise)}`}
            {board?.sunset && ` · sunset ${clock(board.sunset)}`}
          </span>
        </div>
        <div className="row">
          <select className="select select--inline" value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'mine')}>
            <option value="all">All resources</option>
            <option value="mine">{role === 'instructor' ? 'My reservations' : 'My students only'}</option>
          </select>
          {can('reservation:create') && (
            <button className="btn btn--primary" onClick={openBooking}>
              + New reservation
              {waiting > 0 && (
                <span
                  title={`${waiting} request${waiting === 1 ? '' : 's'} waiting to be assigned`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 18, height: 18, padding: '0 5px', marginLeft: 2,
                    borderRadius: 999, fontSize: 11, fontWeight: 700, lineHeight: 1,
                    background: 'rgba(255,255,255,0.28)',
                  }}
                >{waiting}</span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="board">
        {dayQuery.loading && !board ? <Spinner /> : board ? (
          <ResourceGrid board={board} onOpen={setOpenId} />
        ) : null}

        {board && <TodayPanel board={board} onOpen={setOpenId} />}

        {/* The review rail sits under the board, so the timeline keeps the full
            width and the findings can run across it rather than down a column. */}
        <div className="card">
          <div className="card__head">
            <span>
              <span className="card__title">Needs review · {board?.alerts.length ?? 0}</span>
              <span className="card__meta" style={{ display: 'block', marginTop: 2 }}>
                Advisory only. FlightSlots never clears a flight — the pilot in command decides.
              </span>
            </span>
          </div>
          {board
            ? board.alerts.length
              ? <div className="findings--grid"><FindingList findings={board.alerts} onAction={goto} /></div>
              : <div className="empty">Nothing needs review on this day.</div>
            : <Spinner />}
        </div>

      </div>

      {openId && (
        <ReservationDrawer id={openId} onClose={() => setOpenId(null)} onChanged={reload} />
      )}

      {booking && (!assignId || assignQuery.data) && (
        <NewReservation
          request={assignId ? (assignQuery.data as QueuedRequest) : null}
          onClose={closeBooking}
          onDone={() => { closeBooking(); reload(); }}
        />
      )}
    </>
  );
}

/* ── Panels ───────────────────────────────────────────────────────── */

function TodayPanel({ board, onOpen }: { board: DayBoard; onOpen: (id: number) => void }): JSX.Element {
  const { role } = useSession();
  // Collapsed state is a per-viewer preference, so it is remembered locally.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('flightslots.todayPanel') !== 'closed'; } catch { return true; }
  });
  const toggle = () => setOpen((v) => {
    try { localStorage.setItem('flightslots.todayPanel', v ? 'closed' : 'open'); } catch { /* private mode */ }
    return !v;
  });

  const title = role === 'dispatch' ? 'Check-outs due · today'
    : role === 'management' ? 'School reservations · today' : 'My reservations · today';

  const flights = board.reservations.filter((r) => r.status !== 'requested');
  const dual = flights.filter((f) => f.kind === 'dual').reduce((s, f) => s + (f.endsAt - f.startsAt) / 3600000, 0);
  const ground = flights.reduce((s, f) => s + f.groundHours, 0);

  return (
    <div className="card">
      <button className="card__head card__head--toggle" aria-expanded={open} onClick={toggle}>
        <span className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
          <ChevronDown className={open ? 'disclosure disclosure--open' : 'disclosure'} />
          <span className="card__title">{title}</span>
          <span className="card__meta">{flights.length}</span>
        </span>
        <span className="card__meta">{dual.toFixed(1)} flight · {ground.toFixed(1)} ground</span>
      </button>
      {open && (
      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Time</th><th>Student</th><th>Instructor</th><th>Aircraft</th><th>Lesson</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {flights.length === 0 && (
              <tr><td colSpan={7} className="empty">Nothing on the board for this day.</td></tr>
            )}
            {flights.map((r) => (
              <tr key={r.id}>
                <td className="bold">{time(r.startsAt)} – {time(r.endsAt)}</td>
                <td>{r.studentName || <span className="muted">{r.notes || r.kind}</span>}</td>
                {/* A solo or a rental has no instructor, which is a fact about
                    the flight rather than something missing. */}
                <td>{r.instructorName || <span className="muted">{r.kind === 'solo' ? 'Solo' : '—'}</span>}</td>
                <td>{r.aircraftTail}</td>
                <td>{r.lessonLabel || <span className="muted">—</span>}</td>
                <td><Pill>{r.status}</Pill></td>
                <td className="num"><button className="btn btn--link" onClick={() => onOpen(r.id)}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

/** Local wall-clock, 24-hour, the way a schedule desk reads it. */
function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function time(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export { Drawer };
